import { ReactNode } from 'react';

import { ChatbotProvider, type ChatNotice, type TrackedChatEvent } from '@lotlinx-aws/chatbot';
import { useSelectedDealerId } from '@lotlinx-aws/dealer-selector';
import { useAtom } from 'jotai';
import ReactGA from 'react-ga4';

import { userAtom } from 'store';
import { useToast } from '@lotlinx-aws/sparky-component-library/Sparky/Toast';

export interface ChatbotHostProps {
  children: ReactNode;
}

/** Same GA call CoDriver made through `useReactGA().addReactGA(name, options)` */
function trackChatEvent({ gaEventName, payload }: TrackedChatEvent) {
  ReactGA.gtag('event', gaEventName, { event_name: gaEventName, ...payload });
}

/**
 * Configures `@lotlinx-aws/chatbot` (CoDriver's LotGPT chat) with turn4-api, the signed-in user and
 * the selected dealer. Must sit inside `DealerSelectorProvider` and `ToastProvider`.
 */
export default function ChatbotHost({ children }: ChatbotHostProps) {
  const [user] = useAtom(userAtom);
  const [dealerId] = useSelectedDealerId();
  const { addToast } = useToast();

  const notify = ({ status, description }: ChatNotice) =>
    addToast({ status, description, duration: 5000 });

  return (
    <ChatbotProvider
      appId='turn4'
      apiUrl={import.meta.env.VITE_APP_API_URL ?? ''}
      token={user?.token}
      user={
        user ? { id: user.id, email: user.email, first: user.first, last: user.last } : undefined
      }
      dealerId={dealerId}
      onTrack={trackChatEvent}
      onNotify={notify}
    >
      {children}
    </ChatbotProvider>
  );
}
